/* tslint:disable:no-duplicate-string no-identical-functions */
import openIdAuthentication from 'authentication/openIdAuthentication';
import { history } from 'nomst/Stores/Navigation/router.store';
import { ErrorResponse } from 'common/errorResponse';
import { maxUrlLength } from 'common/constants';
import { TeamRoutes } from 'typings/enums';

class HttpService {
    private readonly sameOriginCredential: RequestCredentials = 'same-origin';
    private readonly includeCredential: RequestCredentials = 'include';
    private headers: Headers = new Headers({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'pragma': 'no-cache',
        'cache-control': 'no-cache',
        // This needs to be no store or else IE11 caches the result of everything
        'cache': 'no-store'
    });

    setHeaders(key: string, value: string): Headers {
        this.headers.set(key, value);
        return this.headers;
    }

    async get<T>(url: string, redirectOnFail: boolean = true, includeCredentials: boolean = false): Promise<T|null> {
        const response = await fetch(url, {
            headers: this.headers,
            credentials: includeCredentials ? this.includeCredential : this.sameOriginCredential,
            // This needs to be no store or else IE11 caches the result of everything
            cache: 'no-store'
        }).then((res) => this.validateStatus(res, redirectOnFail));

        return await this.getData<T>(response);
    }

    // used for the salesforce connector that has no auth
    async getSimple<T>(url: string, redirectOnFail: boolean = true): Promise<T|null> {
        const response = await fetch(url).then((res) => this.validateStatus(res, redirectOnFail));

        return await this.getData<T>(response);
    }

    async download<T>(url: string, type: 'GET' | 'POST', data: T,  redirectOnFail: boolean = true): Promise<string> {
        const response = await fetch(url, {
            method: type,
            headers: this.headers,
            credentials: this.sameOriginCredential,
            body: JSON.stringify(data),
            // This needs to be no store or else IE11 caches the result of everything
            cache: 'no-store'
        }).then((res) => this.validateStatus(res, redirectOnFail));

        const blob = await response.blob();
        return window.URL.createObjectURL(blob);
    }

    async post<TRequest, TResponse>(
        url: string, content: TRequest,
        redirectOnFail: boolean = true,
        includeCredentials: boolean = false)
    : Promise<TResponse|null> {
        const response = await fetch(url, {
            headers: this.headers,
            method: 'POST',
            body: JSON.stringify(content),
            credentials: includeCredentials ? this.includeCredential : this.sameOriginCredential,
            // This needs to be no store or else IE11 caches the result of everything
            cache: 'no-store'
        }).then((res) => this.validateStatus(res, redirectOnFail));

        return await this.getData<TResponse>(response);
    }

    async postAsForm<TResponse>(
        url: string, content: FormData, redirectOnFail: boolean = false): Promise<TResponse|null> {
        this.useMultiPartForm();

        const response = await fetch(url, {
            headers: this.headers,
            method: 'POST',
            body: content,
            // This needs to be no store or else IE11 caches the result of everything
            cache: 'no-store'
        }).then((res) => this.validateStatus(res, redirectOnFail));

        this.disuseMultiPartForm();
        return await this.getData<TResponse>(response);
    }

    async put<TRequest, TResponse>(url: string, content: TRequest, redirectOnFail: boolean = true): Promise<TResponse|null> {
        const response = await fetch(url, {
            headers: this.headers,
            method: 'PUT',
            body: JSON.stringify(content),
            credentials: this.sameOriginCredential,
            // This needs to be no store or else IE11 caches the result of everything
            cache: 'no-store'
        }).then((res) => this.validateStatus(res, redirectOnFail));

        return await this.getData<TResponse>(response);
    }

    async deleteWithRequestBody<TRequest, TResponse>(url: string, content: TRequest, redirectOnFail: boolean = true): Promise<TResponse|null> {
        const response = await fetch(url, {
            headers: this.headers,
            method: 'DELETE',
            body: JSON.stringify(content),
            credentials: this.sameOriginCredential,
            // This needs to be no store or else IE11 caches the result of everything
            cache: 'no-store'
        }).then((res) => this.validateStatus(res, redirectOnFail));

        return await this.getData<TResponse>(response);
    }

    async delete<T>(url: string, redirectOnFail: boolean = true): Promise<T|null> {
        const response = await fetch(url, {
            headers: this.headers,
            method: 'DELETE',
            credentials: this.sameOriginCredential,
            // This needs to be no store or else IE11 caches the result of everything
            cache: 'no-store'
        }).then((res) => this.validateStatus(res, redirectOnFail));

        return await this.getData<T>(response);
    }

    displaySaveFileDialog(blobUrl: string, filename: string): void {
        const saveCsvDialog = document.createElement('a');
        document.body.appendChild(saveCsvDialog);
        saveCsvDialog.href = blobUrl;
        saveCsvDialog.download = filename;
        saveCsvDialog.click();
        saveCsvDialog.remove();
    }

    // This method breaks a query url into smaller parts for a query param with a list
    // url is everything up to the equal sign of the list (other query params need to go first)
    // queryList is the full comma-separated list
    buildUrlsForQueryList(url: string, queryList: string): string[] {
        const baseLength = url.length;
        const maxAllowedQueryLength = maxUrlLength - baseLength;
        const urls: string[] = [];
        while (queryList && queryList.length > maxAllowedQueryLength) {
            // Create the first shortened URL
            const lastAllowedComma = queryList.substring(0, maxAllowedQueryLength - 1).lastIndexOf(',');
            const shortenedUrl = `${url}${queryList.substring(0, lastAllowedComma)}`;
            urls.push(shortenedUrl);

            // Trim the used parts of the queryList off to make the next URL
            queryList = queryList.substring(lastAllowedComma + 1);
        }

        if (queryList) {
            urls.push(`${url}${queryList}`);
        }
        return urls;
    }

    private useMultiPartForm(): void {
        this.headers.delete('Content-Type');
    }

    private disuseMultiPartForm(): void {
        this.headers.set('Content-Type', 'application/json');
    }

    private async validateStatus(response: Response, redirectOnFail: boolean = true): Promise<Response> {
        if (response.status >= 200 && response.status < 300) {
            return Promise.resolve(response);
        }

        if (response.status === 401) {
            const url = `${history.location.pathname}${history.location.search}`;
            await openIdAuthentication.handleUnauthorizedCall(url);
            return Promise.reject(response.statusText);
        }

        if (response.status === 403) {
            if (redirectOnFail === false) {
                return Promise.reject(response.statusText);
            }

            if (history.location.pathname.includes(TeamRoutes.AccessDenied) === false) {
                window.location.replace(TeamRoutes.AccessDenied);
            }

            return Promise.reject(response.statusText);
        }

        if (response.status === 500) {
            const date = new Date();
            // TODO - (david) - remove after finished troubleshooting login issue
            // tslint:disable-next-line:no-console
            console.log(date.toLocaleString() + ' Getting Internal Exception');
        }

        if (response.status === 404) {
            return await this.processNotFoundError(response);
        }

        return Promise.reject(response.statusText);
    }

    private async processNotFoundError(response: Response): Promise<Response> {
        const responseText = await response.text();
        const error = JSON.parse(responseText) as ErrorResponse;
        const url = TeamRoutes.NotFound.replace(':code', error.code);
        history.push(url);
        return Promise.reject(response.statusText);
    }

    private async getData<T>(response: Response): Promise<T|null> {
        const responseText = await response.text();
        if (!responseText) {
            return null;
        }

        return JSON.parse(responseText) as T;
    }
}

const httpService = new HttpService();

export default httpService;